import { api } from "./api";
import { ENDPOINTS } from "./endpoints";
import { operations } from "../types/openapi";

type getSettingsOperation = operations["getSettings"];
type setSettingsOperation = operations["setSettings"];
type getRateOperation = operations["getRate"];
type updateSettingsInfoTextOperation = operations["updateInfoText"];
type getWalletsOperation = operations["getWalletTx"];
// type updateWalletsOperation = operations["updateWallets"];
type updateRateOperation = operations["updateRate"];
type getGlobalBalancesOperation = operations["getGlobalBalances"];
type getNotificationSettingsOperation = operations["getNotificationSettings"];
type updateNotificationSettingsOperation = operations["updateNotificationSettings"];
type getWebSettingsOperation = operations["getWebSettings"];
type updateWebSettingsOperation = operations["updateWebSettings"];
type getSmsTemplateOperation = operations["getSmsTemplate"];
type uploadSmsTemplateOperation = operations["uploadSmsTemplate"];
type getMailLayoutOperation = operations["getTemplate"];
type uploadMailLayoutOperation = operations["uploadTemplate"];
type getLogoOperation = operations["getLogo"];
type uploadLogoOperation = operations["uploadLogo"];
type getFaviconOperation = operations["getFavicon"];
type uploadFaviconOperation = operations["uploadFavicon"];
type getCompanySettingsOperation = operations["getCompanySettings"];
type updateCompanySettingsOperation = operations["updateCompanySettings"];
type getCryptoRatesOperation = operations["getAll_3"];
type getAllTooltipsOperation = operations["getAllTooltips"];
type downloadDefaultDocTooltipsOperation = operations["getTooltipsByStatus"];
type uploadTooltipsFile = operations["updateTooltipsByStatus"];
type resetSmsTemplateOperation = operations["resetSmsTemplate"];
type resetMailLayoutOperation = operations["resetMailLayout"];
type getRequestFeeSettingsOperation = operations["getRequestFeeSettings"];
type updateRequestFeeSettingsOperation = operations["updateRequestFeeSettings"];

/**
 * Настройки платформы (доступен для OPERATOR)
 */
export const getCompanyFees = (): Promise<getSettingsOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.getCompanyFees());
};

/**
 * Обновить настройки платформы (доступен для OPERATOR)
 */
export const updateCompanyFees = (
  formData: setSettingsOperation["requestBody"]["content"]["application/json"]
): Promise<setSettingsOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.post(ENDPOINTS.updateCompanyFees(), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Индикативный курс (LIQUIDITY_PROVIDER, OPERATOR, CUSTOMER)
 */
export const getRates = (): Promise<getRateOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.getRates());
};

/**
 * Индикативный курс криптовалют (LIQUIDITY_PROVIDER, OPERATOR, CUSTOMER)
 */
export const getCryptoRates = (
  params: getCryptoRatesOperation["parameters"]["query"]["criteria"]
): Promise<getCryptoRatesOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.getCryptoRates(), params);
};

/**
 * Получение списка кошельков компании (доступно для LIQUIDITY_PROVIDER)
 */
export const getProviderWallets = ({
  id,
}: getWalletsOperation["parameters"]["path"]): Promise<getWalletsOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.providerWallets(id));
};

/**
 * Обновление списка кошельков компании (доступно для LIQUIDITY_PROVIDER)
 */
// export const editProviderWallets = (
//   { id }: updateWalletsOperation["parameters"]["path"],
//   formData: updateWalletsOperation["requestBody"]["content"]["application/json"]
// ): Promise<updateWalletsOperation["responses"]["200"]["content"]> => {
//   return api.put(ENDPOINTS.providerWallets(id), formData, {
//     contentType: "multipart/form-data",
//   });
// };

/**
 * Обновить Индикативный курс (OPERATOR)
 */
export const updateRates = (
  formData: updateRateOperation["requestBody"]["content"]["application/json"]
): Promise<updateRateOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.updateRates(), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Виджет балансы на всех кошельках платформы (OPERATOR)
 */
export const getBalances = (): Promise<getGlobalBalancesOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.globalBalances());
};

/**
 * Обновить информационный текст платформы
 */
export const updateSettingsInfoText = async (
  formData: updateSettingsInfoTextOperation["requestBody"]["content"]["application/json"]
): Promise<void> => {
  const response = await fetch(ENDPOINTS.settingsInfoText(), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api.getToken()}`,
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const error = await response.text().catch(() => response.statusText);
    throw new Error(error);
  }
};

/**
 * Получить настройки уведомлений пользователя
 */
export const getNotificationSettings = ({
  userId,
  companyId,
}: getNotificationSettingsOperation["parameters"]["path"]): Promise<
  getNotificationSettingsOperation["responses"]["200"]["content"]["*/*"]
> => {
  return api.get(ENDPOINTS.notificationSettings(userId, companyId));
};

/**
 * Обновить настройки уведомлений пользователя
 */
export const updateNotificationSettings = (
  { userId, companyId }: updateNotificationSettingsOperation["parameters"]["path"],
  formData: updateNotificationSettingsOperation["requestBody"]["content"]["application/json"]
): Promise<void> => {
  return api.put(ENDPOINTS.notificationSettings(userId, companyId), formData);
};

/**
 * Получить настройки компании
 */
export const getCompanySettings = (
  companyId: string
): Promise<getCompanySettingsOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.doubleActionsSettings(companyId));
};

/**
 * Обновить настройки компании
 */
export const updateCompanySettings = (
  companyId: string,
  formData: updateCompanySettingsOperation["requestBody"]["content"]["application/json"]
): Promise<updateCompanySettingsOperation["responses"]["200"]> => {
  return api.put(ENDPOINTS.doubleActionsSettings(companyId), formData);
};

/**
 * Получить настройки веб-приложения
 */
export const getWebSettings = async (): Promise<getWebSettingsOperation["responses"]["200"]["content"]["*/*"]> => {
  const response = await fetch(ENDPOINTS.settingsWebSettings());

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return response.json();
};

/**
 * Обновить настройки веб-приложения
 */
export const updateWebSettings = (
  formData: updateWebSettingsOperation["requestBody"]["content"]["application/json"]
): Promise<updateWebSettingsOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.put(ENDPOINTS.settingsWebSettings(), formData);
};

/**
 * Получить шаблон SMS
 */
export const getSMSTemplate = (): Promise<getSmsTemplateOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.smsTemplate());
};

/**
 * Загрузить шаблон SMS
 */
export const addSMSTemplate = (
  template: uploadSmsTemplateOperation["requestBody"]["content"]["application/json"]
): Promise<uploadSmsTemplateOperation["responses"]["200"]> => {
  return api.post(ENDPOINTS.smsTemplate(), template);
};
/**
 * Сбросить шаблон SMS
 */
export const resetSMSTemplate = (type: string): Promise<resetSmsTemplateOperation["responses"]["200"][]> => {
  return api.post(ENDPOINTS.resetSmsTemplate(type));
};
/**
 * Получить шаблон email
 */
export const getMailLayout = (): Promise<getMailLayoutOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get<getMailLayoutOperation["responses"]["200"]["content"]["*/*"]>(ENDPOINTS.mailLayout());
};

/**
 * Загрузить шаблон email
 */
export const addMailLayout = (
  layout: uploadMailLayoutOperation["requestBody"]["content"]["application/json"]
): Promise<uploadMailLayoutOperation["responses"]["200"]> => {
  return api.post(ENDPOINTS.mailLayout(), layout);
};
/**
 * Сбросить шаблон email
 */
export const resetMailLayout = (): Promise<resetMailLayoutOperation["responses"]["200"][]> => {
  return api.post(ENDPOINTS.resetMailLayout());
};
/**
 * Получить логотип
 */
export const getLogo = async (): Promise<getLogoOperation["responses"]["200"]["content"]["*/*"]> => {
  const response = await fetch(ENDPOINTS.logo());

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return response.json();
};

/**
 * Загрузить логотип
 */
export const uploadLogo = (formData: any): Promise<uploadLogoOperation["responses"]["200"]["content"]["*/*"][]> => {
  return api.post(ENDPOINTS.logo(), formData);
};

/**
 * Получить favicon
 */
export const getFavicon = async (): Promise<getFaviconOperation["responses"]["200"]["content"]["*/*"]> => {
  const response = await fetch(ENDPOINTS.favicon());

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return response.json();
};

/**
 * Загрузить favicon
 */
export const uploadFavicon = (
  formData: any
): Promise<uploadFaviconOperation["responses"]["200"]["content"]["*/*"][]> => {
  return api.post(ENDPOINTS.favicon(), formData);
};

/**
 * Получить подсказки
 */
export const getAllTooltips = (): Promise<getAllTooltipsOperation["responses"]["200"]["content"]["*/*"][]> => {
  return api.get(ENDPOINTS.getAllTooltips());
};

/**
 * Скачать дефолтный документ подсказки
 */
export const downloadDefaultDocTooltips = (
  status: string
): Promise<downloadDefaultDocTooltipsOperation["responses"]["200"]["content"]["*/*"][]> => {
  return api.get(ENDPOINTS.downloadDefaultDocTooltips(status));
};

/**
 * Загрузка шаблона документа
 */
export const uploadTooltipsFile = ({
  status,
  formData,
}: {
  status: string;
  formData: FormData;
}): Promise<uploadTooltipsFile["responses"]["200"]["content"]> => {
  return api.put(ENDPOINTS.uploadTooltipsFile(status), formData);
};

/**
 * Получить настройки дополнительных комиссий
 */
export const getRequestFeeSettings = (): Promise<
  getRequestFeeSettingsOperation["responses"]["200"]["content"]["*/*"]
> => {
  return api.get(ENDPOINTS.requestFeeSettings());
};

/**
 * Обновить настройки дополнительных комиссий (доступен для SENIOR_OPERATOR)
 */
export const updateRequestFeeSettings = (
  formData: updateRequestFeeSettingsOperation["requestBody"]["content"]["application/json"]
): Promise<void> => {
  return api.put(ENDPOINTS.requestFeeSettings(), formData);
};
